import { BiometricCheckProgress, Country } from '@afterpaytouch/portal-api'
import { Route } from '../config/router'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/identityBiometric'
import { IdentityCheckStatus } from '@afterpay/types'
import { app } from '../env'

export type IdentityDocument = 'licence' | 'medicare' | 'passport'
export type IdentityPiece = 'address'

export const BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL: number = app.BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL ?? 5000
export const BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME: number = app.BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME ?? 90000
export const BIOMETRIC_VERIFYING_TIME_OUT: number = app.BIOMETRIC_VERIFYING_TIME_OUT ?? 300000
export const PERSONA_ENV_ID = app.PERSONA_ENV_ID
export const PERSONA_TEMPLATE_ID = app.PERSONA_TEMPLATE_ID

export interface DocumentProps {
  onSuccess?: () => any
  onFailure?: () => any
}

export const IdentityDocuments = {
  LICENCE: 'licence' as const,
  MEDICARE: 'medicare' as const,
  PASSPORT: 'passport' as const,
} as const

export const toIdentityDocument = (str?: string | string[]): IdentityDocument | null => {
  if (Array.isArray(str)) {
    str = str[0]
  }
  if (typeof str === 'undefined') {
    return null
  }
  switch (str) {
    case 'medicare':
    case 'passport':
    case 'licence':
      return str
    default:
      return null
  }
}

/**
 * Determines the appropriate identity verification route based on country and verification status
 * @param isCountryNeedSoftIdAttempt - Whether the country enabled soft idv (UK/CA/AU)
 * @param identityResponse - Optional identity check response containing verification metadata
 * @returns The appropriate route for identity verification
 */
export const getIdentityVerificationRoute = (
  softIdRetryEnabled = false,
  isCountryNeedSoftIdAttempt: boolean,
  identityResponse?: ConsumerAccountIdentitiesResponse,
  country?: Country
): Route => {
  // for AU while FF is off always return hard idv flow
  if (country === Country.AU && !softIdRetryEnabled) {
    return Route.VERIFY_IDENTITY
  }
  // Early return for non-biometric countries (NZ)
  if (!isCountryNeedSoftIdAttempt || typeof identityResponse === 'undefined') {
    return Route.VERIFY_IDENTITY
  }
  const isPersonaFlow = isCountryUsePersonaFlow(country)
  // Handle UK/CA biometric verification countries
  return getVerificationRouteWithIdentityResponse(identityResponse, isPersonaFlow)
}

/**
 * Determines the route for countries requiring biometric verification (UK/CA)
 * @param identityResponse - Optional identity check response
 * @returns The appropriate biometric verification route
 */
export const getVerificationRouteWithIdentityResponse = (identityResponse: ConsumerAccountIdentitiesResponse, isPersonaFlow = false): Route => {
  // Check if user has profile check attempts remaining
  if (identityResponse.identityCheckMetadata?.profileCheckAttemptsLeft === true) {
    return Route.VERIFY_RETRY
  }

  // for AU they don't have soft id retry attempts left, so we can return hard idv flow
  if (!identityResponse.biometricCheckEnabled) {
    return Route.VERIFY_IDENTITY
  }

  // Determine route based on biometric check progress
  const needsToStartBiometric = shouldStartBiometric(identityResponse, isPersonaFlow)

  return needsToStartBiometric ? Route.VERIFY_IDENTITY_START : Route.VERIFYING_BIOMETRIC
}

export const shouldStartBiometric = (identityResponse?: ConsumerAccountIdentitiesResponse, isPersonaFlow = false): boolean => {
  const progress = identityResponse?.biometricCheckProgress
  return (
    (identityResponse?.biometricCheckEnabled === true && progress === BiometricCheckProgress.NOT_STARTED) ||
    progress === BiometricCheckProgress.CHECK_TERMINATED ||
    (isPersonaFlow && progress === BiometricCheckProgress.CHECK_SUBMITTED)
  )
}

export const isConsumerRequiredToVerifyIdentity = (identityCheckStatus?: IdentityCheckStatus): boolean => {
  return identityCheckStatus === IdentityCheckStatus.NOT_VERIFIED || identityCheckStatus === IdentityCheckStatus.PARTIAL_VERIFIED
}

export const isCountryNeedsSoftIdAttempt = (country?: Country): boolean => {
  if (!country) {
    return false
  }
  return [Country.GB, Country.CA, Country.AU].includes(country)
}
export const isCountryUsePersonaFlow = (country?: Country): boolean => (country ? [Country.CA].includes(country) : false)

export const isCountryNeedsBiometricVerification = (country?: Country): boolean => {
  if (!country) {
    return false
  }
  return [Country.GB, Country.CA].includes(country)
}

export const isCountryHardIdvSkipDocumentSelection = (country?: Country): boolean => (country ? [Country.CA].includes(country) : false)

export const isCountrySkipCreateCheck = (country?: Country): boolean => (country ? [Country.CA].includes(country) : false)
